import React from 'react';

/**
 * LehrerDesk Button — pill-friendly, violet-primary, gentle press-scale.
 * Variants map to the Agento soft-productivity button system.
 */
export function Button({
  children,
  variant = 'primary',
  size = 'md',
  pill = false,
  block = false,
  disabled = false,
  loading = false,
  iconLeft = null,
  iconRight = null,
  style = {},
  ...rest
}) {
  const sizes = {
    sm: { padding: '8px 16px',  font: 'var(--ld-font-size-sm)',   minH: 36, radius: 'var(--ld-radius-md)' },
    md: { padding: '12px 22px', font: 'var(--ld-font-size-base)', minH: 48, radius: 'var(--ld-radius-lg)' },
    lg: { padding: '16px 30px', font: 'var(--ld-font-size-md)',   minH: 54, radius: 'var(--ld-radius-lg)' },
  };
  const s = sizes[size] || sizes.md;

  const variants = {
    primary: {
      background: 'var(--ld-primary)',
      color: 'var(--ld-text-on-primary)',
      boxShadow: 'var(--ld-shadow-button)',
      border: 'none',
    },
    secondary: {
      background: 'var(--ld-secondary-soft)',
      color: 'var(--ld-primary-strong)',
      boxShadow: 'none',
      border: 'none',
    },
    tertiary: {
      background: 'transparent',
      color: 'var(--ld-primary)',
      boxShadow: 'none',
      border: 'none',
    },
    ghost: {
      background: 'transparent',
      color: 'var(--ld-text-secondary)',
      boxShadow: 'none',
      border: '1.5px solid var(--ld-border)',
    },
    danger: {
      background: 'var(--ld-danger-soft)',
      color: 'var(--ld-danger-text)',
      boxShadow: 'none',
      border: 'none',
    },
  };
  const v = variants[variant] || variants.primary;

  return (
    <button
      disabled={disabled || loading}
      style={{
        fontFamily: 'var(--ld-font-family)',
        fontWeight: 'var(--ld-font-weight-semibold)',
        fontSize: s.font,
        lineHeight: 1,
        padding: s.padding,
        minHeight: s.minH,
        borderRadius: pill ? 'var(--ld-radius-pill)' : s.radius,
        display: block ? 'flex' : 'inline-flex',
        width: block ? '100%' : undefined,
        alignItems: 'center',
        justifyContent: 'center',
        gap: 'var(--ld-space-sm)',
        whiteSpace: 'nowrap',
        cursor: disabled || loading ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.5 : 1,
        userSelect: 'none',
        touchAction: 'manipulation',
        transition: 'transform var(--ld-dur-instant) var(--ld-ease), box-shadow var(--ld-transition-fast), background var(--ld-transition-fast), filter var(--ld-transition-fast)',
        ...v,
        ...style,
      }}
      onMouseDown={(e) => { if (!disabled && !loading) e.currentTarget.style.transform = 'scale(0.97)'; }}
      onMouseUp={(e) => { e.currentTarget.style.transform = 'scale(1)'; }}
      onMouseLeave={(e) => { e.currentTarget.style.transform = 'scale(1)'; }}
      {...rest}
    >
      {loading ? <Spinner /> : iconLeft}
      {children}
      {!loading && iconRight}
    </button>
  );
}

function Spinner() {
  return (
    <span
      style={{
        width: 16, height: 16, borderRadius: '50%',
        border: '2px solid currentColor', borderTopColor: 'transparent',
        display: 'inline-block', animation: 'ld-spin 0.7s linear infinite',
      }}
    />
  );
}
